import React, { useContext, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AuthContext } from '../context/AuthContext'
import { courseService } from '../services/apiService'

const Dashboard = () => {
  const { user } = useContext(AuthContext)
  const [enrollments, setEnrollments] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchEnrollments()
  }, [])

  const fetchEnrollments = async () => {
    try {
      const response = await courseService.getEnrollments()
      // Handle paginated response
      const data = response.data.results || response.data
      setEnrollments(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('Error fetching enrollments:', error)
      setEnrollments([])
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
      <div className='mb-8'>
        <h1 className='text-3xl font-bold text-gray-900'>
          Welcome back, {user?.first_name}!
        </h1>
        <p className='mt-2 text-gray-600'>
          Here's what's happening with your courses today.
        </p>
      </div>

      {/* Stats */}
      <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-8'>
        <div className='bg-white rounded-lg shadow p-6'>
          <div className='flex items-center'>
            <div className='flex-shrink-0 bg-primary-100 rounded-md p-3'>
              <svg
                className='h-6 w-6 text-primary-600'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253'
                />
              </svg>
            </div>
            <div className='ml-5'>
              <p className='text-sm font-medium text-gray-500'>
                Enrolled Courses
              </p>
              <p className='text-2xl font-semibold text-gray-900'>
                {enrollments.length}
              </p>
            </div>
          </div>
        </div>

        <div className='bg-white rounded-lg shadow p-6'>
          <div className='flex items-center'>
            <div className='flex-shrink-0 bg-green-100 rounded-md p-3'>
              <svg
                className='h-6 w-6 text-green-600'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
                />
              </svg>
            </div>
            <div className='ml-5'>
              <p className='text-sm font-medium text-gray-500'>Completed</p>
              <p className='text-2xl font-semibold text-gray-900'>
                {enrollments.filter((e) => e.completed).length}
              </p>
            </div>
          </div>
        </div>

        <div className='bg-white rounded-lg shadow p-6'>
          <div className='flex items-center'>
            <div className='flex-shrink-0 bg-yellow-100 rounded-md p-3'>
              <svg
                className='h-6 w-6 text-yellow-600'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
                />
              </svg>
            </div>
            <div className='ml-5'>
              <p className='text-sm font-medium text-gray-500'>In Progress</p>
              <p className='text-2xl font-semibold text-gray-900'>
                {enrollments.filter((e) => !e.completed).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Courses */}
      <div className='bg-white rounded-lg shadow'>
        <div className='px-6 py-5 border-b border-gray-200'>
          <h2 className='text-lg font-semibold text-gray-900'>
            My Enrolled Courses
          </h2>
        </div>
        <div className='p-6'>
          {loading ? (
            <div className='text-center py-8'>
              <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto'></div>
            </div>
          ) : enrollments.length > 0 ? (
            <div className='space-y-4'>
              {enrollments.map((enrollment) => (
                <div
                  key={enrollment.id}
                  className='flex items-center justify-between p-4 border border-gray-200 rounded-lg'
                >
                  <div className='flex-1'>
                    <h3 className='text-lg font-medium text-gray-900'>
                      {enrollment.course.title}
                    </h3>
                    <p className='text-sm text-gray-500'>
                      Progress: {enrollment.progress}%
                    </p>
                    <div className='mt-2 w-full bg-gray-200 rounded-full h-2'>
                      <div
                        className='bg-primary-600 h-2 rounded-full'
                        style={{ width: `${enrollment.progress}%` }}
                      ></div>
                    </div>
                  </div>
                  <Link
                    to={`/courses/${enrollment.course.id}`}
                    className='ml-4 bg-primary-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-700'
                  >
                    Continue
                  </Link>
                </div>
              ))}
            </div>
          ) : (
            <div className='text-center py-8'>
              <p className='text-gray-500 mb-4'>
                You haven't enrolled in any courses yet.
              </p>
              <Link
                to='/courses'
                className='bg-primary-600 text-white px-6 py-2 rounded-md text-sm font-medium hover:bg-primary-700 inline-block'
              >
                Browse Courses
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Dashboard
